if (!customElements.get('alma-sticky-cart')) {
  customElements.define('alma-sticky-cart', class AlmaStickyCart extends HTMLElement {
    connectedCallback() {
      this.productRoot = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
      this.form = document.getElementById(this.dataset.formId);
      this.nativeButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      this.variantSelect = this.querySelector('[data-sticky-variant]');
      this.submitButton = this.querySelector('[data-sticky-submit]');
      this.status = this.querySelector('[data-sticky-status]');
      if (!this.productRoot || !this.form || !this.nativeButton || !this.variantSelect) return;

      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.variantSelect.addEventListener('change', () => this.selectVariant(), listenerOptions);
      this.submitButton.addEventListener('click', () => this.submitNativeForm(), listenerOptions);
      this.productRoot.addEventListener('change', event => {
        if (event.target.closest?.('variant-selects')) this.clearScrollPreservation();
        this.scheduleRefresh();
      }, listenerOptions);
      window.addEventListener('scroll', () => this.scheduleVisibility(), { ...listenerOptions, passive: true });
      window.addEventListener('resize', () => this.scheduleVisibility(), { ...listenerOptions, passive: true });
      this.productObserver = new MutationObserver(mutations => {
        if (mutations.some(mutation => !this.contains(mutation.target))) this.scheduleRefresh();
      });
      this.productObserver.observe(this.productRoot, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'value', 'hidden'],
      });
      this.resizeObserver = new ResizeObserver(() => this.updateReservedSpace());
      this.resizeObserver.observe(this);
      this.refresh();
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.productObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.visibilityFrame);
      clearTimeout(this.selectionTimeout);
      this.clearScrollPreservation();
      document.documentElement.style.removeProperty('--alma-sticky-cart-height');
    }

    clearScrollPreservation() {
      this.productRoot?.querySelector('media-gallery')?.removeAttribute('data-alma-preserve-scroll');
    }

    scheduleRefresh() {
      if (this.refreshFrame) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.refresh();
        this.updateVisibility();
      });
    }

    scheduleVisibility() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateReservedSpace() {
      const height = this.hidden ? 0 : this.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--alma-sticky-cart-height', `${Math.ceil(height)}px`);
    }

    updateVisibility() {
      const nativeButtonVisible = this.nativeButton?.isConnected && this.nativeButton.getClientRects().length > 0;
      const passedButton = nativeButtonVisible && this.nativeButton.getBoundingClientRect().bottom <= 0;
      this.hidden = !passedButton;
      this.updateReservedSpace();
    }

    setStatus(message = '') {
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    refresh() {
      this.form = document.getElementById(this.dataset.formId);
      this.nativeButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      if (!this.form || !this.nativeButton) {
        this.hidden = true;
        this.updateReservedSpace();
        return;
      }
      const nativeVariantId = this.form.querySelector('[name="id"]')?.value || '';
      if (this.pendingVariantId === nativeVariantId) {
        this.pendingVariantId = null;
        clearTimeout(this.selectionTimeout);
        this.clearScrollPreservation();
      }
      if (!this.pendingVariantId) this.variantSelect.value = nativeVariantId;

      const currentOption = this.variantSelect.selectedOptions[0];
      const busy = this.nativeButton.classList.contains('loading');
      const unavailable = !currentOption?.value || currentOption.disabled || this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true';
      this.submitButton.disabled = Boolean(this.pendingVariantId) || unavailable || busy;
      this.submitButton.setAttribute('aria-busy', String(Boolean(this.pendingVariantId) || busy));
      if (this.pendingVariantId || busy) {
        this.submitButton.textContent = this.dataset.loadingLabel;
      } else if (!currentOption?.value) {
        this.submitButton.textContent = this.dataset.unavailableLabel;
      } else if (unavailable) {
        this.submitButton.textContent = this.nativeButton.querySelector('span')?.textContent.trim() || this.dataset.soldOutLabel;
      } else {
        this.submitButton.textContent = this.dataset.addLabel;
      }

      if (currentOption?.value) {
        this.querySelector('[data-sticky-price]').textContent = currentOption.dataset.price;
        this.querySelector('[data-sticky-compare]').textContent = currentOption.dataset.compare;
        this.querySelector('[data-sticky-compare-wrap]').hidden = !currentOption.dataset.compare;
        const image = this.querySelector('.alma-sticky-cart__image');
        if (image && currentOption.dataset.image) {
          image.removeAttribute('srcset');
          image.src = currentOption.dataset.image;
        }
      }
      const errorWrapper = this.productRoot.querySelector('.product-form__error-message-wrapper');
      const errorMessage = errorWrapper && !errorWrapper.hidden
        ? errorWrapper.querySelector('.product-form__error-message')?.textContent.trim()
        : '';
      if (errorMessage) this.setStatus(errorMessage);
    }

    selectVariant() {
      const selectedOption = this.variantSelect.selectedOptions[0];
      const picker = this.productRoot.querySelector('variant-selects');
      if (!selectedOption?.value || selectedOption.disabled || !picker) {
        this.refresh();
        return;
      }
      const nativeVariantId = this.form.querySelector('[name="id"]')?.value;
      if (selectedOption.value === nativeVariantId && !this.pendingVariantId) {
        this.refresh();
        return;
      }

      let variantOptions;
      try {
        variantOptions = JSON.parse(selectedOption.dataset.options);
      } catch {
        this.setStatus(this.dataset.selectionError);
        this.refresh();
        return;
      }
      const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
      const controls = groups.map((group, index) => {
        const select = group.querySelector('select');
        if (select) {
          const option = Array.from(select.options).find(candidate => candidate.value === variantOptions[index]);
          return option ? { select, option } : null;
        }
        const input = Array.from(group.querySelectorAll('input[type="radio"]'))
          .find(candidate => candidate.value === variantOptions[index]);
        return input ? { input } : null;
      });
      if (groups.length !== variantOptions.length || controls.some(control => !control)) {
        this.setStatus(this.dataset.selectionError);
        this.refresh();
        return;
      }
      let changeTarget;
      controls.forEach(control => {
        if (control.select) {
          Array.from(control.select.options).forEach(option => option.toggleAttribute('selected', option === control.option));
          control.select.value = control.option.value;
          changeTarget = control.select;
        } else {
          control.input.checked = true;
          changeTarget = control.input;
        }
      });
      this.pendingVariantId = selectedOption.value;
      this.setStatus();
      clearTimeout(this.selectionTimeout);
      this.selectionTimeout = setTimeout(() => {
        if (!this.pendingVariantId) return;
        this.pendingVariantId = null;
        this.clearScrollPreservation();
        this.setStatus(this.dataset.selectionError);
        this.refresh();
      }, 12000);
      if (typeof this.productRoot.handleOptionValueChange === 'function') {
        this.productRoot.querySelector('media-gallery')?.setAttribute('data-alma-preserve-scroll', '');
        picker.dispatchProductSelectEvent?.();
        this.productRoot.handleOptionValueChange({
          data: {
            event: { target: changeTarget },
            target: this.variantSelect,
            selectedOptionValues: picker.selectedOptionValues,
          },
        });
      } else {
        changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.refresh();
    }

    submitNativeForm() {
      this.refresh();
      if (this.submitButton.disabled || this.pendingVariantId) return;
      this.setStatus();
      this.form.requestSubmit(this.nativeButton);
      this.scheduleRefresh();
    }
  });
}
